import { customAbility } from '@/models/users/user-permissions/custom-ability';
import { UserEntityPermissionsType, UserPermissionAction } from '@/models/users/user-permissions/user-entity-permissions';
import { UserPermissions, UserRoles } from '@/models/users/user-permissions/user-permissions';
import { Ability, RawRule } from '@casl/ability';
import { authenticationService, usersService } from '..';

export default class AuthorizationManagerService {
    private permissions?: UserPermissions;

    constructor() {
        Ability.addAlias(UserPermissionAction.DirectRead, 'read');
        Ability.addAlias(UserPermissionAction.Write, 'crud');
    }

    // #region Generic

    public get isAdmin(): boolean {
        return this.permissions ? this.permissions!.roles.some((role) => role === UserRoles.Admin) : false;
    }

    public get isCommercial(): boolean {
        return this.permissions ? this.permissions!.roles.some((role) => role === UserRoles.Commercial) : false;
    }

    public isBusinessOwner(businessId: number): boolean {
        if (this.permissions) {
            const entityPermission = this.permissions.entities.find((e) => e.entity === UserEntityPermissionsType.Businesses);
            return entityPermission ? entityPermission.owner.some((id) => id === businessId) : false;
        }
        return false;
    }

    public get permissionsLoaded(): boolean {
        return customAbility.rules.length > 0;
    }

    public resetPermissions(): void {
        this.permissions = undefined;
        customAbility.update([]);
    }

    public async refreshPermissions(): Promise<void> {
        this.permissions = await authenticationService.isAuthenticated ? await usersService.getActualUserPermissions() : undefined;
        this.setAbilityRules();
    }

    public can(action: string, subject: any, field?: string): boolean {
        return customAbility.can(action, subject, field);
    }

    public addOrUpdatePermission(action: UserPermissionAction, subject: UserEntityPermissionsType, entityId: number) {
        const rules: RawRule[] = customAbility.rules;

        const existingRule = rules.find((r) => r.subject === UserEntityPermissionsType[subject] && r.actions === action);
        if (existingRule) {
            (existingRule.conditions as any).permissionsIdToCheck.$in.push(entityId);
        } else {
            rules.push({ actions: action, subject: UserEntityPermissionsType[subject], conditions: { permissionsIdToCheck: { $in: [entityId] } } });
        }

        customAbility.update(rules);
    }

    public existsPermission(subject: UserEntityPermissionsType, entityId: number) {
        return customAbility.rules.some(r => r.subject === UserEntityPermissionsType[subject] && (r.conditions as any).permissionsIdToCheck.$in.some((id: number) => id === entityId));
    }

    // #endregion Generic

    // #region User Invitations

    public get canCreateUserInvitations(): boolean {
        return customAbility.can(UserPermissionAction.Owner, 'all') ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Projects] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions));
    }

    public get canListUserInvitations(): boolean {
        return customAbility.can(UserPermissionAction.Owner, 'all') ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Projects] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.UserInvitations] && r.actions === UserPermissionAction.DirectRead && this.conditionsRuleHasAny(r.conditions));
    }

    // #endregion User Invitations

    // #region Associations

    public get canCreateAssociations(): boolean {
        return customAbility.can(UserPermissionAction.Owner, 'all') ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Projects] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions));
    }

    public get canListAssociations(): boolean {
        return customAbility.can(UserPermissionAction.Owner, 'all') ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Projects] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Associations] && r.actions === UserPermissionAction.DirectRead && this.conditionsRuleHasAny(r.conditions));
    }

    public get canWriteOnlySingleAssociation(): boolean {
        return customAbility.rules.some((r) =>
            r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Associations] &&
            r.actions === UserPermissionAction.Write &&
            (r.conditions as any).permissionsIdToCheck.$in.length === 1);
    }

    public get authorizedAssociationsIdsToEdit(): number[] {
        const authorizedIds = new Array();

        customAbility.rules.filter((r) =>
            r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Associations] &&
            r.actions === UserPermissionAction.Write).forEach((rule) => {
                (rule.conditions as any).permissionsIdToCheck.$in.forEach((id: any) => {
                    authorizedIds.push(id);
                });
            });

        return authorizedIds;
    }

    // #endregion Associations

    // #region Campaigns

    public get canCreateCampaign(): boolean {
        return customAbility.can(UserPermissionAction.Owner, 'all') ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Projects] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions));
    }

    public get canListCampaigns(): boolean {
        return customAbility.can(UserPermissionAction.Owner, 'all') ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Projects] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Campaigns] && r.actions === UserPermissionAction.Read && this.conditionsRuleHasAny(r.conditions));
    }

    public get isOwnerOfAnyCampaign(): boolean {
        const authorizedIds = new Array();

        customAbility.rules.filter((r) =>
            r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Campaigns] &&
            r.actions === UserPermissionAction.Owner).forEach((rule) => {
                (rule.conditions as any).permissionsIdToCheck.$in.forEach((id: any) => {
                    authorizedIds.push(id);
                });
            });

        return authorizedIds.length > 0;
    }

    public get isOwnerOfCampaignIds(): number[] {
        const authorizedIds = new Array();

        customAbility.rules
            .filter(r => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Campaigns] && r.actions === UserPermissionAction.Owner)
            .forEach(rule => {
                (rule.conditions as any).permissionsIdToCheck.$in.forEach((id: any) => {
                    authorizedIds.push(id);
                });
            });

        return authorizedIds;
    }

    // #endregion Campaigns

    // #region Business

    public get canCreateBusiness(): boolean {
        return customAbility.can(UserPermissionAction.Owner, 'all') ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Projects] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Associations] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions));
    }

    public get canListBusiness(): boolean {
        return customAbility.can(UserPermissionAction.Owner, 'all') ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Projects] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Associations] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Businesses] && r.actions === UserPermissionAction.DirectRead && this.conditionsRuleHasAny(r.conditions));
    }

    public get canWriteOnlySingleBusiness(): boolean {
        return customAbility.rules.some((r) =>
            r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Businesses] &&
            r.actions === UserPermissionAction.Write &&
            (r.conditions as any).permissionsIdToCheck.$in.length === 1);
    }

    public get canReadMultipleBusinesses(): boolean {
        return customAbility.rules.some((r) =>
            r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Businesses] &&
            r.actions === UserPermissionAction.Read &&
            (r.conditions as any).permissionsIdToCheck.$in.length > 1);
    }

    public get authorizedBusinessIdsToRead(): number[] {
        const authorizedIds = new Array();

        customAbility.rules.filter((r) =>
            r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Businesses] &&
            r.actions === UserPermissionAction.Read).forEach((rule) => {
                (rule.conditions as any).permissionsIdToCheck.$in.forEach((id: any) => {
                    authorizedIds.push(id);
                });
            });

        return authorizedIds;
    }

    public get authorizedBusinessIdsToEdit(): number[] {
        const authorizedIds = new Array();

        customAbility.rules.filter((r) =>
            r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Businesses] &&
            r.actions === UserPermissionAction.Write).forEach((rule) => {
                (rule.conditions as any).permissionsIdToCheck.$in.forEach((id: any) => {
                    authorizedIds.push(id);
                });
            });

        return authorizedIds;
    }

    // #endregion Business

    // #region References

    public get canCreateReferences(): boolean {
        return customAbility.can(UserPermissionAction.Owner, 'all') ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Projects] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Associations] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            // customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Campaigns] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Businesses] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions));
    }

    public get canListReferences(): boolean {
        return customAbility.can(UserPermissionAction.Owner, 'all') ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Projects] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Associations] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Businesses] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.References] && r.actions === UserPermissionAction.DirectRead && this.conditionsRuleHasAny(r.conditions));
    }

    public get authorizedReferenceIdsToEdit(): number[] {
        const authorizedIds = new Array();

        customAbility.rules.filter((r) =>
            r.subject === UserEntityPermissionsType[UserEntityPermissionsType.References] &&
            r.actions === UserPermissionAction.Write).forEach((rule) => {
                (rule.conditions as any).permissionsIdToCheck.$in.forEach((id: any) => {
                    authorizedIds.push(id);
                });
            });

        return authorizedIds;
    }

    // #endregion References

    // #region Business Orders

    public get canListBusinessOrders(): boolean {
        return customAbility.can(UserPermissionAction.Owner, 'all') ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Projects] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Associations] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Businesses] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.BusinessOrders] && r.actions === UserPermissionAction.DirectRead && this.conditionsRuleHasAny(r.conditions));
    }

    // #endregion

    // #region Business Prospects

    public get canListBusinessProspects(): boolean {
        return customAbility.can(UserPermissionAction.Owner, 'all') ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Projects] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.BusinessProspects] && r.actions === UserPermissionAction.DirectRead && this.conditionsRuleHasAny(r.conditions));
    }

    // #endregion

    // #region Business Bookings

    public get canListBusinessBookingRequests(): boolean {
        return customAbility.can(UserPermissionAction.Owner, 'all') ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Projects] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Associations] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Businesses] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.BusinessBookingRequests] && r.actions === UserPermissionAction.DirectRead && this.conditionsRuleHasAny(r.conditions));
    }

    // #endregion

    // #region Business Contacts

    public get canListBusinessContacts(): boolean {
        return customAbility.can(UserPermissionAction.Owner, 'all') ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Projects] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Associations] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Businesses] && r.actions === UserPermissionAction.Write && this.conditionsRuleHasAny(r.conditions)) ||
            customAbility.rules.some((r) => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.BusinessContacts] && r.actions === UserPermissionAction.DirectRead && this.conditionsRuleHasAny(r.conditions));
    }

    // #endregion

    private setAbilityRules() {
        customAbility.update([]);

        if (this.permissions) {
            const newRules: RawRule[] = [];

            if (this.permissions!.roles.some((role) => role === UserRoles.Admin)) {
                newRules.push({ actions: UserPermissionAction.Owner, subject: 'all' });
            } else {
                this.permissions!.entities.forEach((entity) => {
                    newRules.push({ actions: UserPermissionAction.Owner, subject: UserEntityPermissionsType[entity.entity!], conditions: { permissionsIdToCheck: { $in: entity.owner.map((id) => id) } } });
                    newRules.push({ actions: UserPermissionAction.Read, subject: UserEntityPermissionsType[entity.entity!], conditions: { permissionsIdToCheck: { $in: entity.read.map((id) => id) } } });
                    newRules.push({ actions: UserPermissionAction.DirectRead, subject: UserEntityPermissionsType[entity.entity!], conditions: { permissionsIdToCheck: { $in: entity.directRead.map((id) => id) } } });
                    newRules.push({ actions: UserPermissionAction.Write, subject: UserEntityPermissionsType[entity.entity!], conditions: { permissionsIdToCheck: { $in: entity.write.map((id) => id) } } });
                });
            }

            customAbility.update(newRules);
        }
    }

    private conditionsRuleHasAny(conditions: any): boolean {
        if (conditions) {
            return conditions.permissionsIdToCheck.$in.length;
        }
        return false;
    }
}

export const authorizationManagerService = new AuthorizationManagerService();
